import { create } from 'zustand';
import voiceService, { CallStatus, VoiceLine } from '@/services/voice.service';

/**
 * Telefonie-Zustand (Telnyx WebRTC).
 *
 * Der Audiostrom läuft Browser ↔ Telnyx, nicht über unser Backend. Hier liegt
 * nur der Signalisierungs-Client plus der aktive Anruf. Bewusst NICHT persistiert:
 * ein laufendes Gespräch überlebt keinen Reload.
 *
 * Das SDK wird dynamisch geladen — wer nie telefoniert, lädt es auch nicht.
 *
 * Wichtig: `Call`-Objekte des SDK sind KEINE EventEmitter. Sämtliche
 * Zustandswechsel kommen ausschließlich über den `telnyx.notification`-Kanal
 * des Clients (`type === 'callUpdate'`).
 */

export type VoiceConnection = 'idle' | 'connecting' | 'ready' | 'error';

/** Auf die Zustände reduziert, die das UI unterscheiden muss. */
export type ActiveCallPhase = 'dialing' | 'ringing' | 'active' | 'ending';

export type { VoiceLine };

export interface ActiveCall {
  phase: ActiveCallPhase;
  /** Leitung (Organisation), über die der Anruf läuft — Label für Multi-Shop-Agenten. */
  lineOrgId?: string;
  lineLabel?: string;
  /** Gegenstelle in E.164. */
  number: string;
  displayName?: string | null;
  direction: 'outbound' | 'inbound';
  /** ID des Datenbank-Eintrags in `calls` — für spätere Status-Updates. */
  recordId: string | null;
  ticketId?: string | null;
  /** Zeitpunkt der Annahme; erst ab hier läuft die Gesprächsdauer. */
  answeredAt: number | null;
  startedAt: number;
}

interface VoiceState {
  connection: VoiceConnection;
  error: string | null;
  callerId: string | null;
  /** Alle registrierten Leitungen (Organisationen mit aktiver Nummer). */
  lines: VoiceLine[];
  /** Für AUSGEHENDE Anrufe gewählte Leitung (Org-ID). */
  activeLineOrgId: string | null;
  setActiveLine: (orgId: string) => void;
  call: ActiveCall | null;
  muted: boolean;
  held: boolean;
  /** Läuft gerade eine Aufzeichnung des eingehenden Anrufs? */
  recording: boolean;

  connect: () => Promise<void>;
  /** Nach Leitungsänderungen (Nummer zugewiesen/gekauft) neu registrieren. */
  reconnect: () => Promise<void>;
  toggleRecording: () => Promise<void>;
  disconnect: () => void;
  dial: (opts: { number: string; displayName?: string | null; ticketId?: string | null; customerEmail?: string | null; lineOrgId?: string }) => Promise<void>;
  answer: () => void;
  hangup: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  sendDtmf: (digit: string) => void;
}

/* Nicht-reaktive Handles: gehören nicht in den Store-State (nicht serialisierbar,
   und jede Zuweisung würde ein Re-Render auslösen). */
/** Leitung (Org-ID) → TelnyxRTC-Client. ALLE Leitungen sind gleichzeitig
    registriert — ein eingehender Anruf klingelt auf dem Client der Leitung
    (des Shops), deren Nummer gewählt wurde. */
const clients = new Map<string, any>();
let currentCall: any = null;
let currentCallOrgId: string | null = null;
/** Ein verstecktes <audio> je Leitung — remoteElement ist pro Client. */
const audioEls = new Map<string, HTMLAudioElement>();
let connectPromise: Promise<void> | null = null;
/* Bleibt ein Wahlversuch ohne jede Rückmeldung hängen (blockiertes Mikrofon,
   abgelehnter INVITE), soll das UI nicht ewig „Wird gewählt" zeigen. */
let dialWatchdog: number | null = null;

const DIAL_TIMEOUT_MS = 30_000;

function clearWatchdog() {
  if (dialWatchdog !== null) {
    window.clearTimeout(dialWatchdog);
    dialWatchdog = null;
  }
}
/* Das Anlegen des Protokolleintrags läuft asynchron. Kurze Anrufe (wählen und
   sofort auflegen) enden, bevor die ID da ist — deshalb wird auf diese Zusage
   gewartet, statt auf einen State-Wert zu schauen, der noch null ist. */
let recordIdPromise: Promise<string | null> | null = null;

/**
 * Verstecktes <audio> je Leitung, damit Ton unabhängig von gemounteten
 * Komponenten läuft. Pro Leitung ein eigenes Element — jedes SDK-Client-Objekt
 * braucht sein eigenes remoteElement. Geführt wird trotzdem immer nur EIN
 * Gespräch gleichzeitig.
 */
function remoteAudio(orgId: string): HTMLAudioElement {
  const existing = audioEls.get(orgId);
  if (existing) return existing;
  const el = document.createElement('audio');
  el.autoplay = true;
  el.setAttribute('playsinline', 'true');
  el.style.display = 'none';
  document.body.appendChild(el);
  audioEls.set(orgId, el);
  return el;
}

/** Telnyx-Zustände auf die vier UI-Phasen abbilden. */
function toPhase(state: string): ActiveCallPhase | null {
  switch (state) {
    case 'new':
    case 'requesting':
    case 'trying':
    case 'recovering':
      return 'dialing';
    case 'ringing':
    case 'early':
    case 'answering':
      return 'ringing';
    case 'active':
    case 'held':
      return 'active';
    case 'hangup':
    case 'destroy':
    case 'purge':
      return 'ending';
    default:
      return null;
  }
}

const useVoiceStore = create<VoiceState>((set, get) => {
  /** Anrufprotokoll fortschreiben — Fehler hier dürfen das Gespräch nie stören. */
  const patchRecord = (status: CallStatus, extra: Record<string, any> = {}) => {
    const pending = recordIdPromise;
    if (!pending) return;
    pending
      .then((id) => (id ? voiceService.updateCall(id, { status, ...extra }) : null))
      .catch(() => {});
  };

  /** Protokolleintrag anlegen und die ID nachtragen, sobald der Server antwortet. */
  const logCall = (payload: Parameters<typeof voiceService.startCall>[0], sdkCallId: string) => {
    recordIdPromise = voiceService
      .startCall(payload)
      .then((rec) => {
        const cur = get().call;
        // Inzwischen kann ein anderer Anruf laufen — nur den passenden ergänzen.
        if (cur && currentCall?.id === sdkCallId) set({ call: { ...cur, recordId: rec.id } });
        return rec.id;
      })
      .catch(() => null);
  };

  const finish = (answered: boolean, cause?: string) => {
    clearWatchdog();
    patchRecord(answered ? 'completed' : 'no_answer', cause ? { hangup_cause: cause } : {});
    currentCall = null;
    currentCallOrgId = null;
    recordIdPromise = null;
    set({ call: null, muted: false, held: false, recording: false });
  };

  /** Gescheiterter Versuch: Protokoll als `failed` schließen und Grund zeigen. */
  const failCall = (message: string, cause: string) => {
    clearWatchdog();
    patchRecord('failed', { hangup_cause: cause });
    try { void Promise.resolve(currentCall?.hangup?.()).catch(() => {}); } catch { /* nie aufgebaut */ }
    currentCall = null;
    currentCallOrgId = null;
    recordIdPromise = null;
    set({ call: null, muted: false, held: false, recording: false, error: message });
  };

  /**
   * Einziger Ort, an dem Anrufzustände verarbeitet werden. Läuft für ein- und
   * ausgehende Anrufe gleichermaßen.
   */
  const handleCallUpdate = (orgId: string, sdkCall: any, notification: any) => {
    const existing = get().call;

    // Noch kein Anruf im Store: nur eingehende übernehmen. Ausgehende werden
    // bereits in dial() synchron übernommen, bevor die erste Meldung eintrifft.
    if (!existing) {
      if (sdkCall.direction !== 'inbound' || toPhase(sdkCall.state) !== 'ringing') return;

      currentCall = sdkCall;
      currentCallOrgId = orgId;
      const from = notification?.displayNumber
        || sdkCall.options?.remoteCallerNumber
        || sdkCall.options?.callerNumber
        || '';
      const line = get().lines.find((l) => l.organization_id === orgId);

      set({
        call: {
          phase: 'ringing',
          // Über welche Leitung kommt der Anruf herein — bei mehreren Shops
          // sieht der Agent sofort, für wen er abnimmt.
          lineOrgId: orgId,
          lineLabel: line ? `${line.organization_name} · ${line.number}` : undefined,
          number: from,
          displayName: notification?.displayName || sdkCall.options?.remoteCallerName || null,
          direction: 'inbound',
          recordId: null,
          answeredAt: null,
          startedAt: Date.now(),
        },
        muted: false,
        held: false,
      });

      // Kein logCall: eingehende Anrufe protokolliert der Server über den
      // Call-Control-Webhook (auch verpasste, wenn kein Browser offen ist) —
      // ein zweiter Eintrag hier wäre ein Duplikat.
      return;
    }

    // Paralleler zweiter Anruf: ignorieren, es wird immer nur einer geführt.
    if (currentCall && sdkCall.id !== currentCall.id) return;

    const phase = toPhase(sdkCall.state);
    if (!phase) return;

    // Sobald sich überhaupt etwas bewegt, ist der Wachhund überflüssig.
    if (phase !== 'dialing') clearWatchdog();

    if (phase === 'ending') {
      finish(Boolean(existing.answeredAt), sdkCall.cause ? String(sdkCall.cause) : undefined);
      return;
    }

    const held = sdkCall.state === 'held';

    if (phase === 'active' && !existing.answeredAt) {
      set({ call: { ...existing, phase: 'active', answeredAt: Date.now() }, held });
      patchRecord('active');
      return;
    }

    if (phase !== existing.phase) {
      set({ call: { ...existing, phase }, held });
      if (phase === 'ringing') patchRecord('ringing');
      return;
    }

    if (held !== get().held) set({ held });
  };

  /** Eine Leitung registrieren: eigener Client, eigenes Audio-Element. */
  const registerLine = async (line: VoiceLine): Promise<void> => {
    const { TelnyxRTC } = await import('@telnyx/webrtc');
    // SIP-Login der Connection statt JWT: On-Demand-Credentials (JWT-
    // Quelle) sind laut Telnyx-Doku outbound-only — eingehende Anrufe an
    // sie enden mit SIP 403. Nur diese Identität kann klingeln.
    const rtc = new TelnyxRTC({ login: line.login, password: line.password });
    rtc.remoteElement = remoteAudio(line.organization_id);

    await new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        rtc.off('telnyx.ready', onReady);
        rtc.off('telnyx.error', onError);
        clearTimeout(timer);
      };
      const onReady = () => { cleanup(); resolve(); };
      const onError = (e: any) => {
        cleanup();
        reject(new Error(e?.error?.message || e?.message || 'Telnyx-Verbindung fehlgeschlagen'));
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Zeitüberschreitung bei der Telnyx-Anmeldung'));
      }, 20000);
      rtc.on('telnyx.ready', onReady);
      rtc.on('telnyx.error', onError);
      rtc.connect();
    });

    rtc.on('telnyx.notification', (n: any) => {
      if (n?.type === 'callUpdate' && n.call) handleCallUpdate(line.organization_id, n.call, n);
    });

    // Dauerhaft — nicht nur beim Verbinden. Ohne diesen Listener bleibt
    // ein gescheiterter Anruf stumm im Zustand „wird gewählt" stehen.
    rtc.on('telnyx.error', (e: any) => {
      const message = e?.error?.message || e?.message || 'Telefoniefehler';
      // Nur der Client des LAUFENDEN Anrufs darf ihn abbrechen — ein Fehler
      // auf einer anderen Leitung beendet kein fremdes Gespräch.
      if (get().call && currentCallOrgId === line.organization_id) failCall(message, 'sdk_error');
      else set({ error: message });
    });

    rtc.on('telnyx.socket.close', () => {
      clients.delete(line.organization_id);
      if (clients.size === 0) set({ connection: 'idle' });
    });

    clients.set(line.organization_id, rtc);
  };

  const teardownAll = () => {
    clearWatchdog();
    try { currentCall?.hangup?.(); } catch { /* schon beendet */ }
    for (const rtc of clients.values()) {
      try { rtc?.disconnect?.(); } catch { /* nie verbunden */ }
    }
    clients.clear();
    currentCall = null;
    currentCallOrgId = null;
  };

  return {
    connection: 'idle',
    error: null,
    callerId: null,
    lines: [],
    activeLineOrgId: null,
    call: null,
    muted: false,
    held: false,
    recording: false,

    async connect() {
      if (get().connection === 'ready') return;
      // Mehrfach-Aufrufe (zwei Komponenten mounten gleichzeitig) teilen sich einen Versuch.
      if (connectPromise) return connectPromise;

      connectPromise = (async () => {
        set({ connection: 'connecting', error: null });
        try {
          const lines = await voiceService.getLines();
          if (!lines.length) {
            // Keine aktive Leitung — kein Fehler, das UI blendet Telefonie aus.
            // Bewusst 'ready' statt 'idle': der Auto-Connect-Effekt feuert bei
            // 'idle' erneut und würde sonst endlos gegen /lines anlaufen.
            set({ connection: 'ready', lines: [], activeLineOrgId: null, callerId: null });
            return;
          }

          // Alle Leitungen parallel registrieren; eine kaputte Leitung darf
          // die übrigen nicht blockieren.
          const results = await Promise.allSettled(lines.map((l) => registerLine(l)));
          const active = lines.filter((_, i) => results[i].status === 'fulfilled');
          if (!active.length) {
            const first = results[0] as PromiseRejectedResult;
            throw new Error(first?.reason?.message || 'Telnyx-Verbindung fehlgeschlagen');
          }

          // Auswahl beibehalten, sonst erste Leitung.
          const prev = get().activeLineOrgId;
          const selected = active.find((l) => l.organization_id === prev) || active[0];
          set({
            connection: 'ready',
            lines: active,
            activeLineOrgId: selected.organization_id,
            callerId: selected.number,
            error: null,
          });
        } catch (e: any) {
          set({ connection: 'error', error: e?.response?.data?.message || e?.message || 'Verbindung fehlgeschlagen' });
        } finally {
          connectPromise = null;
        }
      })();

      return connectPromise;
    },

    async reconnect() {
      if (get().call) return; // laufendes Gespräch nie abreißen
      teardownAll();
      set({ connection: 'idle', call: null, muted: false, held: false, recording: false });
      await get().connect();
    },

    setActiveLine(orgId: string) {
      const line = get().lines.find((l) => l.organization_id === orgId);
      if (!line) return;
      set({ activeLineOrgId: orgId, callerId: line.number });
    },

    disconnect() {
      teardownAll();
      set({ connection: 'idle', call: null, muted: false, held: false, recording: false });
    },

    async dial({ number, displayName, ticketId, customerEmail, lineOrgId }) {
      if (get().call) throw new Error('Es läuft bereits ein Gespräch');
      if (get().connection !== 'ready') await get().connect();
      if (get().connection !== 'ready') throw new Error(get().error || 'Telefonie nicht verbunden');

      // Leitung wählen: explizit > zuletzt gewählt > erste. Die Nummer der
      // Leitung wird zur Caller-ID des ausgehenden Anrufs.
      const wantedOrg = lineOrgId || get().activeLineOrgId;
      const line = get().lines.find((l) => l.organization_id === wantedOrg) || get().lines[0];
      const lineClient = line ? clients.get(line.organization_id) : null;
      if (!line || !lineClient) throw new Error('Keine aktive Leitung verfügbar');

      // Mikrofon VOR dem Wählen anfordern. Ohne Freigabe kommt das SDK nicht
      // über den Verbindungsaufbau hinaus und meldet nichts — der Anruf hinge
      // dann ohne erkennbaren Grund fest.
      try {
        const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
        probe.getTracks().forEach((track) => track.stop());
      } catch (e: any) {
        const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
        throw new Error(
          denied
            ? 'Mikrofonzugriff wurde blockiert. Bitte im Browser für diese Seite erlauben.'
            : 'Kein Mikrofon gefunden oder nicht verfügbar.',
        );
      }

      const callerId = line.number;
      const call = lineClient.newCall({
        destinationNumber: number,
        callerNumber: callerId || undefined,
        audio: true,
        video: false,
      });

      // Sofort übernehmen: die erste callUpdate-Meldung kann schon unterwegs sein,
      // und handleCallUpdate übernimmt von sich aus nur eingehende Anrufe.
      currentCall = call;
      currentCallOrgId = line.organization_id;
      clearWatchdog();
      dialWatchdog = window.setTimeout(() => {
        if (get().call?.phase === 'dialing') {
          failCall('Keine Antwort von der Telefonie — Anruf konnte nicht aufgebaut werden.', 'dial_timeout');
        }
      }, DIAL_TIMEOUT_MS);

      set({
        error: null,
        call: {
          phase: 'dialing',
          lineOrgId: line.organization_id,
          lineLabel: `${line.organization_name} · ${line.number}`,
          number,
          displayName: displayName || null,
          direction: 'outbound',
          recordId: null,
          ticketId: ticketId || null,
          answeredAt: null,
          startedAt: Date.now(),
        },
        muted: false,
        held: false,
      });

      // Protokolleintrag erst nach dem Wählen — ein fehlschlagendes Log darf
      // den Anruf nicht verhindern.
      logCall(
        {
          direction: 'outbound',
          from_number: callerId,
          to_number: number,
          ticket_id: ticketId || null,
          customer_email: customerEmail || null,
          telnyx_call_id: call.id || null,
        },
        call.id,
      );
    },

    answer() {
      // answer/hangup/hold liefern Promises: ohne catch würde ein bereits
      // beendeter Anruf eine unbehandelte Rejection werfen.
      try { void Promise.resolve(currentCall?.answer?.()).catch(() => {}); } catch { /* Anruf schon weg */ }
    },

    hangup() {
      const existing = get().call;
      try { void Promise.resolve(currentCall?.hangup?.()).catch(() => {}); } catch { /* schon beendet */ }
      if (existing) finish(Boolean(existing.answeredAt), 'local_hangup');
    },

    toggleMute() {
      if (!currentCall) return;
      const next = !get().muted;
      try { next ? currentCall.muteAudio?.() : currentCall.unmuteAudio?.(); } catch { return; }
      set({ muted: next });
    },

    toggleHold() {
      if (!currentCall) return;
      const next = !get().held;
      try {
        void Promise.resolve(next ? currentCall.hold?.() : currentCall.unhold?.()).catch(() => {});
      } catch { return; }
      set({ held: next });
    },

    sendDtmf(digit: string) {
      try { currentCall?.dtmf?.(digit); } catch { /* nicht im Gespräch */ }
    },

    async toggleRecording() {
      const { call, recording } = get();
      // Nur eingehende Anrufe: ausgehende laufen über die Credential Connection
      // ohne Call-Control-Leg — dort gibt es serverseitig nichts zu steuern.
      if (!call || call.direction !== 'inbound' || call.phase !== 'active') return;
      if (recording) {
        await voiceService.stopRecording();
        set({ recording: false });
      } else {
        await voiceService.startRecording();
        set({ recording: true });
      }
    },
  };
});

export default useVoiceStore;
